import * as React from "react";
import {faQuestionCircle} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}  from "@fortawesome/react-fontawesome";
import {cx}               from "emotion";
import {Redirect}         from "react-router-dom";

import "./form-content.scss";

import {consumeContext}                        from "@Common/react/context";
import {Link, makeChain, valueHelper as value} from "@Common/react/cst2";
import {Types}                                 from "@Common/types";
import {DatePicker2}                           from "@Common/ui-components/date-picker-2/date-picker-2";
import {SearchableSelect}                      from "@Common/ui-components/searchable-select/searchable-select";
import {SliderCheckbox}                        from "@Common/ui-components/slider-checkbox/slider-checkbox";
import {parseDateFormat}                       from "@Common/utils/date/parse-date-format";

import {COMPANY_LEVEL, BRANCH_LEVEL} from "../../../../common/logic/user-level";
import {makeBranchAsSublabelMap}     from "../../../../common/select-user-panel/make-branch-as-sublabel-map";
import {mapAgentAsLabel}             from "../../../../common/select-user-panel/map-agent-as-label";
import {mapAgentAsSublabel}          from "../../../../common/select-user-panel/map-agent-as-sublabel";
import {mapBranchAsLabel}            from "../../../../common/select-user-panel/map-branch-as-label";
import {mapSearchAsBranchFilter}     from "../../../../common/select-user-panel/map-search-as-branch-filter";

type Agent                   = Types.Apis.Payloads.Next.Agent.GetAllUsers.Response[number];
type Branch                  = Types.Apis.Payloads.Next.Branch.GetAll.Response[number];
type ListingModel            = Types.Xpresso.Account.Admin.Listings.ListingModel;
type ListingFormContentProps = Types.Xpresso.Account.Admin.Listings.ListingFormContentProps;
type SectionKey              = keyof Types.Xpresso.Account.Admin.Listings.ListingFormContentVisibleSectionsModel;

export const ListingFormContent = ({
    apmSupport,
    errorRef,
    fields,
    sectionsVisible,
    onSectionSwitch,
    validating,
}:ListingFormContentProps) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("session"))
    .then(...consumeContext("userLevels"))
    .then(({session, userLevels}, next:Link<JSX.Element>) => (
        userLevels.userLevel === BRANCH_LEVEL && session?.data?.branch && !session.data.branch.allowAddListing ? (
            <Redirect to="/account"/>
        ) : (
            next()
        )
    ))
    .then("makeInput", value(({userLevels}) => makeInputFactory({errorRef, fields, userLevels, validating})))
    .then("makeLabeledInput", value(({makeInput}) => makeLabeledInputFactory(makeInput)))
    .then("makeSectionSwitch", value(({}) => makeSectionSwitchFactory({onSectionSwitch, sectionsVisible})))
    .then(({makeLabeledInput, makeInput, makeSectionSwitch, userLevels}) => (<div className="listing-form-content-0w4g">
        <div className="section-header" xd-qa-type="label" xd-qa-name={userLevels.userLevel === COMPANY_LEVEL ? "Select Office and User" : "Select User"}>
            <h3>Select {userLevels.userLevel === COMPANY_LEVEL ? "Office and User" : "User"}</h3>
            <p>If you are creating a new listing, please begin by selecting {userLevels.userLevel === COMPANY_LEVEL ? "an office & user" : "a user"}</p>
        </div>
        <div className="input-card" id="office-and-user" xd-qa-type="card" xd-qa-name="Office and User">
            <div className="input-grid">
                {userLevels.userLevel === COMPANY_LEVEL && <div className="input-group">
                    <label xd-qa-type="label" xd-qa-name="Office">Office</label>
                    {makeInput({
                        path: "branch",
                        type: "select",
                        mapSearchAsItemFilter: mapSearchAsBranchFilter,
                        floatingLabel: "Office",
                        className: "office",
                        xdQaName: "Office",
                    })}
                </div>}
                <div className="input-group">
                    <label xd-qa-type="label" xd-qa-name="User">User</label>
                    {makeInput({path: "agent", type: "select", floatingLabel: "User", xdQaName: "User"})}
                </div>
            </div>
        </div>

        <hr/>

        <div className="section-header" xd-qa-type="card" xd-qa-name="Location / Recipient Data Header">
            <h3 xd-qa-type="label" xd-qa-name="Location / Recipient Data">Location / Recipient Data</h3>
        </div>
        <div className="input-card" id="location-and-recipient" xd-qa-type="card" xd-qa-name="Location / Recipient Data">
            <div className="input-grid">
                <div className="input-group">
                    <label xd-qa-type="label" xd-qa-name="Listing Address, Unit #">
                        Listing Address, Unit #
                    </label>
                    {makeInput({
                        path: "locationStreetAddress",
                        type: "text",
                        placeholder: "Listing Address",
                        xdQaName: "Listing Address",
                    })}
                    {makeInput({path: "locationUnitNumber", type: "text", placeholder: "Unit #", xdQaName: "Unit #"})}
                </div>
                <div className="input-group">
                    <label xd-qa-type="label" xd-qa-name="City, State, Zip">City, State, Zip</label>
                    {makeInput({path: "locationCity", type: "text", placeholder: "City", xdQaName: "City"})}
                    {makeInput({path: "locationState", type: "select", floatingLabel: "State", xdQaName: "State"})}
                    {makeInput({path: "locationZip", type: "text", placeholder: "Zip", xdQaName: "Zip"})}
                </div>
                {apmSupport && <div className="input-group checkbox-input-group">
                    <label xd-qa-type="label" xd-qa-name="Notify User via email">
                        {makeInput({path: "eventNotifyAgent", type: "checkbox", xdQaName: "Notify User via email"})}
                        Notify User via email
                        <span aria-label="Selecting this option will send an APM notification email to the user" className="tooltip-host"
                            xd-qa-type="icon" xd-qa-name="Question Circle">&nbsp;<FontAwesomeIcon icon={faQuestionCircle}/></span>
                    </label>
                </div>}
            </div>
        </div>

        <hr/>

        <div className="section-header" xd-qa-type="card" xd-qa-name="Location Data Header">
            <h3 xd-qa-type="label" xd-qa-name="Location Data">Location Data</h3>
            {makeSectionSwitch("location-data")}
        </div>
        {sectionsVisible['location-data'] && (<div className="input-card" id="location-data" xd-qa-type="card" xd-qa-name="Location Data">
            <div className="input-grid">
                <div className="input-group">
                    {makeLabeledInput("MLS ID", {path: "mlsId", type: "text"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("MLS Name", {path: "mlsName", type: "text"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Listing Type", {path: "listingType", type: "select"})}
                </div>
                <div className="input-group">
                    {fields.listingType.value === "jl" ? (
                        makeLabeledInput("Listed Price", {path: "listedPrice", type: "number", min: 0, step: 1000})
                    ) : (
                        makeLabeledInput("Sold Price", {path: "soldPrice", type: "number", min: 0, step: 1000})
                    )}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Date Listed", {path: "dateListed", type: "date"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Date Sold or Closed", {path: "dateSold", type: "date"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Open House Date", {path: "openHouseDate", type: "date"})}
                </div>
                <div className="input-group">
                    <label className="always" xd-qa-type="label" xd-qa-name="Open House Time">Open House Time</label>
                    {makeInput({
                        path: "openHouseStartTime",
                        type: "time",
                        placeholder: "Start Time",
                        xdQaName: "Open House Start Time"
                    })}
                    {makeInput({
                        path: "openHouseEndTime",
                        type: "time",
                        placeholder: "End Time",
                        xdQaName: "Open House End Time"
                    })}
                </div>
            </div>
        </div>)}

        <hr/>

        <div className="section-header" xd-qa-type="card" xd-qa-name="Basic Details Header">
            <h3 xd-qa-type="label" xd-qa-name="Basic Details">Basic Details</h3>
            {makeSectionSwitch("basic-details")}
        </div>
        {sectionsVisible['basic-details'] && (<div className="input-card" id="basic-details" xd-qa-type="card" xd-qa-name="Basic Details">
            <div className="input-grid">
                <h4 xd-qa-type="label" xd-qa-name="Listing">Listing</h4>
                <div className="input-group">
                    {makeLabeledInput("Listing Email", {path: "agentEmailAddress", type: "text"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Listing URL", {path: "listingUrl", type: "text"})}
                </div>
            </div>

            <hr/>

            <div className="input-grid">
                <h4 xd-qa-type="label" xd-qa-name="Buyer/Recipient">Buyer/Recipient</h4>
                <div className="input-group">
                    {makeLabeledInput("Buyer/Recipient First Name", {path: "primaryBuyerFirstName", type: "text"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Buyer/Recipient Last Name", {path: "primaryBuyerLastName", type: "text"})}
                </div>
            </div>

            <hr/>

            <div className="input-grid">
                <h4 xd-qa-type="label" xd-qa-name="Property Info">Property Info</h4>
                <div className="input-group">
                    {makeLabeledInput("Title", {path: "title", type: "text"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Property Type", {path: "propertyType", type: "text", xdQaName: "Title Property Type"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Description", {path: "description", type: "textarea"})}
                </div>
            </div>

            <hr/>

            <div className="input-grid">
                <h4 xd-qa-type="label" xd-qa-name="Additional Details">Additional Details</h4>
                <div className="input-group">
                    {makeLabeledInput("Virtual Tour", {path: "virtualTourUrl", type: "text", xdQaName: "Virtual Tour"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Bedrooms", {path: "bedrooms", type: "select", xdQaName: "Bedrooms"})}
                    {makeLabeledInput("Room Count", {path: "roomCount", type: "select", xdQaName: "Room Count"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Live Area Square Feet", {path: "lotSizeSquareFeet", type: "number", xdQaName: "Live Area Square Feet"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Lot Size", {path: "lotSize", type: "text", xdQaName: "Lot Size"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Year Built", {path: "yearBuilt", type: "text", xdQaName: "Year Built"})}
                </div>
                <div className="input-group">
                    {makeLabeledInput("Fireplace", {path: "firePlace", type: "select", xdQaName: "Fireplace"})}
                </div>
            </div>
        </div>)}

        <hr/>

        <div className="section-header" xd-qa-type="card" xd-qa-name="Property Photos Header">
            <h3 xd-qa-type="label" xd-qa-name="Property Photos">Property Photos</h3>
            {makeSectionSwitch("property-photos")}
        </div>
        {sectionsVisible['property-photos'] && (<div className="input-card" id="property-photos" xd-qa-type="card" xd-qa-name="Property Photos">
            <div className="input-grid">
                {new Array(10).fill(null).map((_, index:0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9) => (
                    <div key={index} className="input-group">
                        {makeLabeledInput(`Picture ${index + 1}`, {path: `picture${index}`, type: "text"})}
                    </div>
                ))}
            </div>
        </div>)}
    </div>))
    .run()
);

const agentByLastNameSort = (a:Agent, b:Agent) => mapAgentAsFullName(a).localeCompare(mapAgentAsFullName(b));

const branchByNameSort = (a:Branch, b:Branch) => a.branchName.localeCompare(b.branchName);

const mapAgentAsFullName = (a:Agent) => [a.agentNameLast && `${a.agentNameLast},`, a.agentNameFirst, a.agentNameMiddle].filter(Boolean).join(" ");

const makeFieldOptionAsLabelMap = <Path extends keyof ListingModel>(path:Path) => {
    switch (path) {
        case "agent"       : return mapAgentAsLabel;
        case "branch"      : return mapBranchAsLabel;
        case "firePlace"   : return (value:boolean) => value ? "Yes" : "No";
        case "listingType" : return (value:string) => value === "jl" ? "Just Listed" : "Just Sold";
    }
};

const makeFieldOptionAsSubLabelMap = <Path extends keyof ListingModel>({forInternalUser, path}:{
    forInternalUser : boolean,
    path            : Path,
}) => {
    switch (path) {
        case "agent": return forInternalUser ? mapAgentAsSublabel : null;
        case "branch": return makeBranchAsSublabelMap({forInternalUser});
    }
};

const makeFieldOptionSelectedFilter = <Path extends keyof ListingModel>({path, value}:{
    path: Path,
    value: ListingModel[Path],
}) => {
    switch (path) {
        case "agent"  : return (agent:Agent) => agent.idAgent === (value as Agent)?.idAgent;
        case "branch" : return (branch:Branch) => branch.idBranch === (value as Branch)?.idBranch;
        default: return (option:any) => option === value;
    }
};

const makeFieldOptionsSort = <Path extends keyof ListingModel>(path:Path) => {
    switch (path) {
        case "agent"  : return agentByLastNameSort;
        case "branch" : return branchByNameSort;
    }
};

const makeInputFactory = ({errorRef, fields, userLevels, validating}:{
    errorRef   : (element:HTMLElement) => void,
    fields     : ListingFormContentProps['fields'],
    userLevels : Types.Xpresso.Loaders.ContextNameMap['userLevels'],
    validating : boolean,
}) => {
    let errorRefAssigned = false;
    errorRef?.(null); // reset error ref, in case of no errors

    return ({path, placeholder, floatingLabel, type, xdQaName, ...props}:{
        path: keyof Types.Xpresso.Account.Admin.Listings.ListingModel,
        type: "checkbox" | "date" | "number" | "select" | "text" | "textarea" | "time",
        xdQaName: string,
        [k:string]: any
    }) => {
        const field:Types.Xpresso.Account.Admin.Listings.FieldProps<typeof path> = fields[path],
              input                                                              = (type === "checkbox") ? (
            <SliderCheckbox
                disabled={field.disabled}
                onChange={(value:boolean) => field.onInput(value as Types.Xpresso.Account.Admin.Listings.ListingModel[typeof path])}
                value={field.value as boolean}
                xdQaName={xdQaName}
                {...props}
            />
        ) : type === "date" ? (
            <DatePicker2
                dateFormat={parseDateFormat("yyyy-mm-dd")}
                state={{
                    onChange: field.onInput,
                    value: field.value,
                }}
                disabled={field.disabled}
                className="datepicker"
                xdQaName={xdQaName}
                {...props}
            />
        ) : type === "textarea" ? (
            <textarea
                name={path}
                disabled={field.disabled}
                onChange={(e) => field.onInput(e.target.value)}
                value={field.value as string}
                xd-qa-type="text_field"
                xd-qa-name={xdQaName}
            />
        ) : ["number", "text", "time"].includes(type) ? (
            <input
                key={path}
                name={path}
                type={type}
                disabled={field.disabled}
                onChange={(e) => {
                    const newValue = e.target[type === "text" ? "value" : "valueAsNumber"] as Types.Xpresso.Account.Admin.Listings.ListingModel[typeof path];
                    field.onInput(typeof newValue === "number" && isNaN(newValue) ? null : newValue);
                }}
                value={type === "time" ? numberAsTimeValue(field.value as number) : (field.value || "") as string}
                step={type === "time" ? 300 : undefined}
                xd-qa-type={type === "time" ? "time_input" : "text_field"}
                xd-qa-name={xdQaName}
                {...props}
            />
        ) : (
            <SearchableSelect<ListingModel[typeof path]>
                list={field.selectOptions?.sort(makeFieldOptionsSort(path) ?? originalSort)}
                disabled={field.disabled}
                isSelected={makeFieldOptionSelectedFilter({path, value: field.value})}
                onSelect={(option) => field.onInput(option)}
                getLabel={makeFieldOptionAsLabelMap(path) as any}
                getSublabel={makeFieldOptionAsSubLabelMap({path, forInternalUser: userLevels.internalUser}) as any}
                xdQaName={xdQaName}
                placeholder={placeholder ? `Select ${placeholder}` : ""}
                {...props}
            />
        );

        const error = validating && field.error;

        // only assign ref to first error
        const assignRef  = Boolean(!errorRefAssigned && error);
        errorRefAssigned = errorRefAssigned || assignRef;

        return (
            <div
                key={path}
                ref={assignRef && ((ref) => errorRef(ref)) || undefined}
                className={cx("input-slot", {'required': field.required})}
            >
                {(placeholder ?? floatingLabel) && (
                    <label
                        key="label"
                        className={cx(!!field.value || field.value === false ? "edgy" : "placeholder", type)}
                    >{placeholder ?? floatingLabel}</label>
                )}
                {input}
                {error && <span className="error" xd-qa-type="label" xd-qa-name={xdQaName + " Error"}>{field.error}</span>}
            </div>
        );
    };
};

const makeLabeledInputFactory = (makeInput:ReturnType<typeof makeInputFactory>) => {
    return (label:string, inputProps:Pick<Parameters<typeof makeInput>[0], "path" | "type"> & {[k:string]: any}) => (<>
        <label xd-qa-type="label" xd-qa-name={label}>{label}</label>
        {makeInput({placeholder: label, xdQaName: label, ...inputProps})}
    </>);
};

const makeSectionSwitchFactory = ({onSectionSwitch: handleSectionSwitch, sectionsVisible}:Pick<ListingFormContentProps, "onSectionSwitch" | "sectionsVisible">) => (
    handleSectionSwitch ? (section:SectionKey) => (
        <a onClick={() => handleSectionSwitch(section)} xd-qa-type="link" xd-qa-name={sectionsVisible[section] ? "Hide Details" : "Show Details"}>
            {sectionsVisible[section] ? "Hide Details" : "Show Details"}
        </a>
    ) : () => false
);

const numberAsTimeValue = (number:number) => (
    number ? new Date(number).toLocaleTimeString("en-US", {hourCycle: "h23", timeZone: "UTC"}) : ""
);

const originalSort = () => 1;

